/**
 * AdminManagementView
 *
 * Super-admin-only panel for overseeing every admin account. Lists all admins
 * with role/status/last-login, and offers per-admin actions: view login
 * history, reset password, enable/disable, and delete (cascades to the admin's
 * games — confirmed loudly).
 *
 * Rendered as a full-screen overlay from DashboardLayout, mirroring
 * GamesListView. All data/actions come from useAdminManagement.
 */

import { useState, useCallback } from 'react';
import { ConfirmModal } from '../ui/ConfirmModal';
import { Modal, Segmented } from '@swtorbit/ui/react';
import {
  GameType,
  GAME_METADATA,
  DEFAULT_ADMIN_PERMISSIONS,
  type AdminPermissions,
} from '@swtorbit/shared-socket-events';
import { useAuthContext } from '../contexts/AuthContext';
import { useAdminManagement } from '../hooks/useAdminManagement';
import { formatDate } from '../utils/formatDate';
import type { AdminSummary, AdminLoginEvent } from '../services/adminManagementApi';

interface AdminManagementViewProps {
  onBack: () => void;
}

/** Format an ISO date string (or null) for display. */
function fmt(iso: string | null): string {
  if (!iso) return '—';
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? '—' : formatDate(ms);
}

type PendingAction = { admin: AdminSummary; action: 'disable' | 'enable' | 'delete' | 'reset-trl-usage' };

export function AdminManagementView({ onBack }: AdminManagementViewProps) {
  const { username: currentUsername } = useAuthContext();
  const {
    admins,
    isLoading,
    error,
    addAdmin,
    resetPassword,
    resetUsage,
    setDisabled,
    removeAdmin,
    loadLoginHistory,
  } = useAdminManagement();

  // Create-admin modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [createUsername, setCreateUsername] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createSuper, setCreateSuper] = useState(false);
  // Game-access permissions for the new admin.
  const [gameAccessMode, setGameAccessMode] = useState<'all' | 'select'>('all');
  const [selectedGameModes, setSelectedGameModes] = useState<GameType[]>([]);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createBusy, setCreateBusy] = useState(false);

  // Login-history modal state
  const [historyAdmin, setHistoryAdmin] = useState<AdminSummary | null>(null);
  const [historyEvents, setHistoryEvents] = useState<AdminLoginEvent[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Password-reset modal state
  const [pwAdmin, setPwAdmin] = useState<AdminSummary | null>(null);
  const [pwValue, setPwValue] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwDone, setPwDone] = useState(false);

  // Disable/enable/delete confirmation state
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const openCreate = useCallback(() => {
    setCreateUsername('');
    setCreatePassword('');
    setCreateSuper(false);
    setGameAccessMode('all');
    setSelectedGameModes([]);
    setCreateError(null);
    setCreateOpen(true);
  }, []);

  const toggleGameMode = useCallback((mode: GameType) => {
    setSelectedGameModes((prev) =>
      prev.includes(mode) ? prev.filter((m) => m !== mode) : [...prev, mode],
    );
  }, []);

  const submitCreate = useCallback(async () => {
    const username = createUsername.trim();
    if (username.length < 3) {
      setCreateError('Username must be at least 3 characters');
      return;
    }
    if (createPassword.length < 8) {
      setCreateError('Password must be at least 8 characters');
      return;
    }
    // Super-admins always get full access; otherwise honour the game-access choice.
    let permissions: AdminPermissions = DEFAULT_ADMIN_PERMISSIONS;
    if (!createSuper && gameAccessMode === 'select') {
      if (selectedGameModes.length === 0) {
        setCreateError('Select at least one game type');
        return;
      }
      permissions = { gameAccess: { mode: 'select', gameModes: selectedGameModes } };
    }
    setCreateBusy(true);
    setCreateError(null);
    try {
      await addAdmin(username, createPassword, createSuper, permissions);
      setCreateOpen(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create admin');
    } finally {
      setCreateBusy(false);
    }
  }, [createUsername, createPassword, createSuper, gameAccessMode, selectedGameModes, addAdmin]);

  const openHistory = useCallback(async (admin: AdminSummary) => {
    setHistoryAdmin(admin);
    setHistoryEvents([]);
    setHistoryError(null);
    setHistoryLoading(true);
    try {
      setHistoryEvents(await loadLoginHistory(admin.id));
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : 'Failed to load login history');
    } finally {
      setHistoryLoading(false);
    }
  }, [loadLoginHistory]);

  const openPasswordReset = useCallback((admin: AdminSummary) => {
    setPwAdmin(admin);
    setPwValue('');
    setPwError(null);
    setPwDone(false);
  }, []);

  const submitPasswordReset = useCallback(async () => {
    if (!pwAdmin) return;
    if (pwValue.length < 8) {
      setPwError('Password must be at least 8 characters');
      return;
    }
    setPwBusy(true);
    setPwError(null);
    try {
      await resetPassword(pwAdmin.id, pwValue);
      setPwDone(true);
      setPwValue('');
    } catch (err) {
      setPwError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setPwBusy(false);
    }
  }, [pwAdmin, pwValue, resetPassword]);

  const confirmPendingAction = useCallback(async () => {
    if (!pending) return;
    setActionBusy(true);
    setActionError(null);
    try {
      if (pending.action === 'delete') {
        await removeAdmin(pending.admin.id);
      } else if (pending.action === 'reset-trl-usage') {
        await resetUsage(pending.admin.id, GameType.TRAILBLAZERS);
      } else {
        await setDisabled(pending.admin.id, pending.action === 'disable');
      }
      setPending(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionBusy(false);
    }
  }, [pending, removeAdmin, resetUsage, setDisabled]);

  return (
    <div className="p-lg text-white">
      <div className="d-flex align-items-center gap-lg mb-lg">
        <button className="back-button" onClick={onBack}>
          ← Back to Dashboard
        </button>
        <h2>Admin Management</h2>
        <button className="btn ms-auto" onClick={openCreate}>
          + Create Admin
        </button>
      </div>

      {error && <div className="text-red mb-sm">{error}</div>}

      <div className="bg-space border-blue-light rounded-base overflow-hidden">
        {/* Header row */}
        <div className="d-flex align-items-center gap-sm p-sm border-bottom fw-bold text-slate text-uppercase fs-xs">
          <div className="flex-1">Username</div>
          <div className="w-100 mw-md text-center">Role</div>
          <div className="w-100 mw-md text-center">Status</div>
          <div className="flex-1">Last Login</div>
          <div className="flex-1">Created</div>
          <div className="w-100 mw-2xl text-end">Actions</div>
        </div>

        {isLoading ? (
          <div className="text-center text-slate p-xl">Loading admins…</div>
        ) : admins.length === 0 ? (
          <div className="text-center text-slate p-xl">No admins found.</div>
        ) : (
          admins.map((admin) => {
            const isSelf = admin.username === currentUsername;
            return (
              <div key={admin.id} className="d-flex align-items-center gap-sm p-sm border-bottom">
                <div className="flex-1">
                  {admin.username}
                  {isSelf && <span className="text-slate fs-xs"> (you)</span>}
                </div>
                <div className="w-100 mw-md text-center">
                  {admin.is_super_admin ? (
                    <span className="text-amber fs-xs text-uppercase">Super</span>
                  ) : (
                    <span className="text-slate fs-xs text-uppercase">Admin</span>
                  )}
                </div>
                <div className="w-100 mw-md text-center">
                  {admin.disabled ? (
                    <span className="text-red fs-xs text-uppercase">Disabled</span>
                  ) : (
                    <span className="text-green-dark fs-xs text-uppercase">Active</span>
                  )}
                </div>
                <div className="flex-1 fs-sm">
                  {fmt(admin.last_login_at)}
                  {admin.last_login_ip && (
                    <span className="text-slate fs-xs"> · {admin.last_login_ip}</span>
                  )}
                </div>
                <div className="flex-1 fs-sm">{fmt(admin.created_at)}</div>
                <div className="w-100 mw-2xl d-flex gap-2xs justify-content-end flex-wrap">
                  <button className="btn btn--sm btn--outline btn--slate" onClick={() => openHistory(admin)}>
                    History
                  </button>
                  <button className="btn btn--sm btn--outline btn--slate" onClick={() => openPasswordReset(admin)}>
                    Reset PW
                  </button>
                  <button
                    className="btn btn--sm btn--outline btn--slate"
                    onClick={() => setPending({ admin, action: 'reset-trl-usage' })}
                  >
                    Reset TRL Usage
                  </button>
                  {!isSelf && (
                    admin.disabled ? (
                      <button
                        className="btn btn--sm btn--green"
                        onClick={() => setPending({ admin, action: 'enable' })}
                      >
                        Enable
                      </button>
                    ) : (
                      <button
                        className="btn btn--sm btn--amber"
                        onClick={() => setPending({ admin, action: 'disable' })}
                      >
                        Disable
                      </button>
                    )
                  )}
                  {!isSelf && (
                    <button
                      className="btn btn--sm btn--red"
                      onClick={() => setPending({ admin, action: 'delete' })}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="d-flex justify-content-between align-items-center mt-sm pt-sm border-top">
        <span className="fs-sm text-slate">{admins.length} admin(s)</span>
      </div>

      {/* Create admin modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} className="admin-create-modal" title="Create Admin" footer={
<>
          <button className="btn btn--outline btn--slate" onClick={() => setCreateOpen(false)} disabled={createBusy}>
            Cancel
          </button>
          <button className="btn" onClick={submitCreate} disabled={createBusy}>
            {createBusy ? 'Creating…' : 'Create Admin'}
          </button>
        </>
}>
          <div className="d-flex flex-column gap-xs">
            <label className="fs-sm text-white" htmlFor="admin-new-username">
              Username (min 3 characters)
            </label>
            <input
              className="input"
              id="admin-new-username"
              type="text"
              autoComplete="off"
              value={createUsername}
              onChange={(e) => setCreateUsername(e.target.value)}
            />
            <label className="fs-sm text-white mt-xs" htmlFor="admin-create-password">
              Password (min 8 characters)
            </label>
            <input
              className="input"
              id="admin-create-password"
              type="password"
              autoComplete="new-password"
              value={createPassword}
              onChange={(e) => setCreatePassword(e.target.value)}
            />
            <label className="d-flex align-items-center gap-2xs fs-sm text-white mt-xs">
              <input
                className="checkbox"
                type="checkbox"
                checked={createSuper}
                onChange={(e) => setCreateSuper(e.target.checked)}
              />
              Grant super-admin (can manage all admins)
            </label>

            {/* Permissions — Game Access. Super-admins implicitly get full access. */}
            {!createSuper && (
              <div className="d-flex flex-column gap-xs mt-xs pt-xs border-top">
                <span className="fs-sm text-white fw-bold">Game Access</span>
                <Segmented
                  size="sm"
                  aria-label="Game access"
                  options={[
                    { value: 'all', label: 'All games' },
                    { value: 'select', label: 'Select games' },
                  ]}
                  value={gameAccessMode}
                  onChange={setGameAccessMode}
                />
                <span className="fs-xs text-white">
                  {gameAccessMode === 'all'
                    ? 'This admin can create and manage every game type.'
                    : 'This admin can only create and manage the game types you select below.'}
                </span>
                {gameAccessMode === 'select' && (
                  <div className="d-flex flex-column gap-2xs">
                    {Object.values(GAME_METADATA).map((meta) => (
                      <label
                        key={meta.id}
                        className="d-flex align-items-center gap-2xs fs-sm text-white"
                      >
                        <input
                          className="checkbox"
                          type="checkbox"
                          checked={selectedGameModes.includes(meta.id)}
                          onChange={() => toggleGameMode(meta.id)}
                        />
                        <span>
                          {meta.icon} {meta.title}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            {createError && <div className="text-red fs-sm">{createError}</div>}
          </div>
        </Modal>

      {/* Login history modal */}
      <Modal open={!!historyAdmin} onClose={() => setHistoryAdmin(null)} className="admin-history-modal" title={<>Login History — {historyAdmin?.username}</>} footer={
<>
          <button className="btn btn--outline btn--slate" onClick={() => setHistoryAdmin(null)}>
            Close
          </button>
        </>
}>
          {historyLoading ? (
            <div className="text-center text-slate p-xl">Loading…</div>
          ) : historyError ? (
            <div className="text-red">{historyError}</div>
          ) : historyEvents.length === 0 ? (
            <div className="text-center text-slate p-xl">No login attempts recorded.</div>
          ) : (
            <div className="d-flex flex-column gap-2xs">
              <div className="d-flex align-items-center gap-sm fw-bold text-slate text-uppercase fs-xs">
                <div className="w-100 mw-md text-center">Result</div>
                <div className="flex-1">Time</div>
                <div className="flex-1">IP</div>
              </div>
              {historyEvents.map((ev) => (
                <div key={ev.id} className="d-flex align-items-center gap-sm fs-sm border-bottom">
                  <div className="w-100 mw-md text-center">
                    {ev.success ? (
                      <span className="text-green-dark fs-xs text-uppercase">OK</span>
                    ) : (
                      <span className="text-red fs-xs text-uppercase">Fail</span>
                    )}
                  </div>
                  <div className="flex-1">{fmt(ev.created_at)}</div>
                  <div className="flex-1">{ev.ip_address ?? '—'}</div>
                </div>
              ))}
            </div>
          )}
        </Modal>

      {/* Password reset modal */}
      <Modal open={!!pwAdmin} onClose={() => setPwAdmin(null)} className="admin-password-modal" title={<>Reset Password — {pwAdmin?.username}</>} footer={
<>
          {pwDone ? (
            <button className="btn" onClick={() => setPwAdmin(null)}>
              Done
            </button>
          ) : (
            <>
              <button className="btn btn--outline btn--slate" onClick={() => setPwAdmin(null)} disabled={pwBusy}>
                Cancel
              </button>
              <button className="btn" onClick={submitPasswordReset} disabled={pwBusy}>
                {pwBusy ? 'Saving…' : 'Set Password'}
              </button>
            </>
          )}
        </>
}>
          {pwDone ? (
            <div className="text-green-dark">Password updated.</div>
          ) : (
            <div className="d-flex flex-column gap-xs">
              <label className="fs-sm text-slate" htmlFor="admin-new-password">
                New password (min 8 characters)
              </label>
              <input
                className="input"
                id="admin-new-password"
                type="password"
                autoComplete="new-password"
                value={pwValue}
                onChange={(e) => setPwValue(e.target.value)}
              />
              {pwError && <div className="text-red fs-sm">{pwError}</div>}
            </div>
          )}
        </Modal>

      {/* Disable / enable / delete confirmation */}
      <ConfirmModal
        isOpen={!!pending}
        onConfirm={confirmPendingAction}
        onCancel={() => {
          setPending(null);
          setActionError(null);
        }}
        title={
          pending?.action === 'delete'
            ? 'Delete Admin'
            : pending?.action === 'disable'
              ? 'Disable Admin'
              : pending?.action === 'reset-trl-usage'
                ? 'Reset Usage Counter'
                : 'Enable Admin'
        }
        message={
          <div className="d-flex flex-column gap-xs">
            {pending?.action === 'delete' ? (
              <>
                <span>
                  Permanently delete <strong>{pending?.admin.username}</strong>?
                </span>
                <span className="text-red">
                  This also deletes ALL of their games, rounds and players. This cannot be undone.
                </span>
              </>
            ) : pending?.action === 'disable' ? (
              <span>
                Disable <strong>{pending?.admin.username}</strong>? They will be signed out and unable
                to log in until re-enabled.
              </span>
            ) : pending?.action === 'reset-trl-usage' ? (
              <span>
                Reset all Trailblazers image usage counts for <strong>{pending?.admin.username}</strong>?
                This cannot be undone.
              </span>
            ) : (
              <span>
                Re-enable <strong>{pending?.admin.username}</strong>? They will be able to log in again.
              </span>
            )}
            {actionError && <span className="text-red">{actionError}</span>}
          </div>
        }
        confirmText={
          actionBusy
            ? 'Working…'
            : pending?.action === 'delete'
              ? 'Delete'
              : pending?.action === 'disable'
                ? 'Disable'
                : pending?.action === 'reset-trl-usage'
                  ? 'Reset'
                  : 'Enable'
        }
        variant={pending?.action === 'enable' ? 'primary' : 'danger'}
      />
    </div>
  );
}
